import React, { Suspense, useEffect, lazy } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { CssBaseline, CircularProgress } from '@mui/material';

// Keep critical imports for initial render
import MainLayout from './layouts/main/MainLayout';
import { LandingLayout } from "./layouts/landing-layout/LandingLayout";
import Analytics from './components/common/Analytics';
import CookieConsent from './components/common/CookieConsent';
import AuthenticationHandler from './components/common/AuthenticationHandler';
import UpdateNotification from './components/common/UpdateNotification';
import InstallPrompt from './components/common/InstallPrompt';
import PullToRefresh from './components/common/PullToRefresh';
import NetworkStatusIndicator from './components/common/NetworkStatusIndicator';
import ErrorBoundary from './components/common/ErrorBoundary';
import AuthErrorBoundary from './components/common/AuthErrorBoundary';
import ProtectedRoute from './components/common/ProtectedRoute';
import FeatureGuard from './components/common/FeatureGuard';
import { checkVersionAndClearCache } from './services/versionManager';
import SecureStorageService from './services/secureStorageService';
import keyboardHandler from './utils/keyboardHandler';

// Lazy load all page components for better performance
// General/Landing pages
const LandingPage = lazy(() => import('./pages/general/LandingPage').then(m => ({ default: m.LandingPage })));
const ContactPage = lazy(() => import('./pages/general/ContactPage'));
const AboutUsPage = lazy(() => import('./pages/general/AboutUsPage'));
const AllFeaturesPage = lazy(() => import('./pages/general/AllFeaturesPage'));
const WhyWorldExplorePage = lazy(() => import('./pages/general/WhyWorldExplorePage'));
const PartnerOnboardingPage = lazy(() => import('./pages/general/PartnerOnboardingPage'));
const ImprintPage = lazy(() => import('./pages/general/ImprintPage'));
const PrivacyPage = lazy(() => import('./pages/general/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/general/TermsPage'));
const NotFoundPage = lazy(() => import('./pages/general/NotFoundPage'));

// Common pages
const NotAuthorized = lazy(() => import('./pages/common/NotAuthorized'));

// Guide pages
const Guides = lazy(() => import('./pages/guides/Guides'));
const GuideContent = lazy(() => import('./pages/guides/GuideContent'));
const GuideCreationPage = lazy(() => import('./pages/guides/GuideCreationPage'));
const TouristGuideForm = lazy(() => import('./components/guides/TouristGuideForm'));

// Admin pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const UserManagementPage = lazy(() => import('./pages/admin/UserManagementPage'));
const PartnerManagementPage = lazy(() => import('./pages/admin/PartnerManagementPage'));
const CodeManagementPage = lazy(() => import('./pages/admin/CodeManagementPage'));
const FeatureManagementPage = lazy(() => import('./pages/admin/FeatureManagementPage'));
const AdminSubscriptionManagementPage = lazy(() => import('./pages/admin/AdminSubscriptionManagementPage'));

// Partner pages
const PartnerDashboard = lazy(() => import('./pages/partner/PartnerDashboard'));
const PropertyOverviewPage = lazy(() => import('./pages/partner/PropertyOverview'));
const PropertyCodesPage = lazy(() => import('./pages/partner/PropertyCodesPage'));
const PropertyUsersPage = lazy(() => import('./pages/partner/PropertyUsersPage'));
const PropertySettingsPage = lazy(() => import('./pages/partner/PropertySettingsPage'));
const PartnerApiManagementPage = lazy(() => import('./pages/partner/api-management/PartnerApiManagementPage'));
const GuestTripsPage = lazy(() => import('./pages/partner/GuestTripsPage'));
const SubscriptionManagementPage = lazy(() => import('./pages/partner/SubscriptionManagementPage'));
const SubscriptionSuccessPage = lazy(() => import('./pages/partner/SubscriptionSuccessPage'));
const SubscriptionCancelPage = lazy(() => import('./pages/partner/SubscriptionCancelPage'));

// User pages
const UserProfilePage = lazy(() => import('./pages/user/UserProfilePage'));
const MyTripsPage = lazy(() => import('./pages/user/MyTripsPage'));
const TripDashboardPage = lazy(() => import('./pages/user/TripDashboardPage'));
const EventsFeedPage = lazy(() => import('./pages/user/EventsFeedPage'));
const PackingListPage = lazy(() => import('./pages/user/PackingListPage'));
const PreArrivalFormPage = lazy(() => import('./pages/user/PreArrivalFormPage'));

// Guest pages
const GuestTripPortal = lazy(() => import('./pages/guest/GuestTripPortal'));

// A tiny helper to scroll to the top on each route change.
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  // One-time migration and initialization
  useEffect(() => {
    // Migrate old localStorage data to SecureStorageService
    SecureStorageService.migrateFromLocalStorage();

    // Check for app version changes and clear cache if needed
    checkVersionAndClearCache().catch(() => {
      // Silent error handling - don't break the app if version check fails
    });

    // Initialize keyboard handler for PWA
    keyboardHandler.init();

    // Cleanup on unmount
    return () => {
      keyboardHandler.destroy();
    };
  }, []);

  const handleRefresh = async () => {
    // Implement refresh logic
    return new Promise((resolve) => {
      setTimeout(() => {
        window.location.reload();
        resolve();
      }, 1000);
    });
  };

  // Centered loading fallback for lazy-loaded routes
  const LoadingFallback = () => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        width: '100%',
      }}
    >
      <CircularProgress color="primary" size={48} />
    </div>
  );

  return (
    <ErrorBoundary>
      <CssBaseline />
      <AuthenticationHandler />
      <NetworkStatusIndicator />
      <ScrollToTop />

      <PullToRefresh onRefresh={handleRefresh}>
        <Suspense fallback={<LoadingFallback />}>
          <AuthErrorBoundary>
            <Routes>
            {/* Landing routes */}
            <Route element={<LandingLayout />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/partner-onboarding" element={<PartnerOnboardingPage />} />
              <Route path="/imprint" element={<ImprintPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/about-us" element={<AboutUsPage />} />
              <Route path="/all-features" element={<AllFeaturesPage />} />
              <Route path="/why-worldexplore" element={<WhyWorldExplorePage />} />
            </Route>

            {/* Guides - Protected routes requiring authentication */}
            <Route 
              path="/guides" 
              element={<MainLayout showUserSidebar={true} showFooter={false}><Guides /></MainLayout>} 
            />
            <Route 
              path="/guides/create/:code" 
              element={<MainLayout showUserSidebar={true} showFooter={false}><GuideCreationPage /></MainLayout>} 
            />
            <Route 
              path="/guideContent" 
              element={<MainLayout showUserSidebar={true} showFooter={false}><GuideContent /></MainLayout>} 
            />
            
            {/* Guest Trip Portal - Public route for guests */}
            <Route path="/trip/:accessToken" element={<GuestTripPortal />} />

            {/* Public Routes with main layout */}
            <Route element={<MainLayout showFooter={false} />}>
              <Route path="/touristGuideForm" element={<TouristGuideForm />} />
              <Route path="/not-authorized" element={<NotAuthorized />} />
            </Route>

            {/* Protected User Routes */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute roles={['User', 'PartnerAdmin', 'Admin']}>
                  <MainLayout showUserSidebar={true} showFooter={false}><UserProfilePage /></MainLayout>
                </ProtectedRoute>
              }
            />
            
            {/* My Trips route with user sidebar - handles its own authentication with login prompt */}
            <Route path="/my-trips" element={<MainLayout showUserSidebar={true} showFooter={false}><MyTripsPage /></MainLayout>} />
            
            {/* Trip Dashboard - detailed view for a specific trip */}
            <Route 
              path="/my-trips/:tripId" 
              element={
                <ProtectedRoute roles={['User', 'PartnerAdmin', 'Admin']}>
                  <MainLayout showUserSidebar={true} showFooter={false}>
                    <FeatureGuard 
                      featureName="guest-trip-management"
                      customMessage="Guest Trip Management is not enabled. This feature allows access to trip dashboards with weather, events, packing lists, and more."
                    >
                      <TripDashboardPage />
                    </FeatureGuard>
                  </MainLayout>
                </ProtectedRoute>
              } 
            />
            
            {/* Trip Events Feed */}
            <Route 
              path="/my-trips/:tripId/events" 
              element={
                <ProtectedRoute roles={['User', 'PartnerAdmin', 'Admin']}>
                  <MainLayout showUserSidebar={true} showFooter={false}>
                    <FeatureGuard 
                      featureName="guest-trip-management"
                      customMessage="Guest Trip Management is not enabled. This feature allows you to view local events for your trip."
                    >
                      <EventsFeedPage />
                    </FeatureGuard>
                  </MainLayout>
                </ProtectedRoute>
              } 
            />
            
            {/* Trip Packing List */}
            <Route 
              path="/my-trips/:tripId/packing" 
              element={
                <ProtectedRoute roles={['User', 'PartnerAdmin', 'Admin']}>
                  <MainLayout showUserSidebar={true} showFooter={false}>
                    <FeatureGuard 
                      featureName="guest-trip-management"
                      customMessage="Guest Trip Management is not enabled. This feature provides AI-powered packing lists for your trips."
                    >
                      <PackingListPage />
                    </FeatureGuard>
                  </MainLayout>
                </ProtectedRoute>
              } 
            />
            
            {/* Trip Pre-Arrival Form */}
            <Route 
              path="/my-trips/:tripId/pre-arrival-form" 
              element={
                <ProtectedRoute roles={['User', 'PartnerAdmin', 'Admin']}>
                  <MainLayout showUserSidebar={true} showFooter={false}>
                    <FeatureGuard 
                      featureName="guest-trip-management"
                      customMessage="Guest Trip Management is not enabled. This feature allows you to complete pre-arrival forms before your trip."
                    >
                      <PreArrivalFormPage />
                    </FeatureGuard>
                  </MainLayout>
                </ProtectedRoute>
              } 
            />

            {/* Admin Protected */}
            <Route
              path="/adminDashboard"
              element={
                <ProtectedRoute roles={['Admin']}>
                  <MainLayout showSidebar={true}><AdminDashboard /></MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/partnerManagement"
              element={
                <ProtectedRoute roles={['Admin']}>
                  <MainLayout showSidebar={true}><PartnerManagementPage /></MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription-management"
              element={
                <ProtectedRoute roles={['Admin']}>
                  <MainLayout showSidebar={true}><AdminSubscriptionManagementPage /></MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/codeManagement"
              element={
                <ProtectedRoute roles={['Admin']}>
                  <MainLayout showSidebar={true}><CodeManagementPage /></MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/userManagement"
              element={
                <ProtectedRoute roles={['Admin']}>
                  <MainLayout showSidebar={true}><UserManagementPage /></MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feature-management"
              element={
                <ProtectedRoute roles={['Admin']}>
                  <MainLayout showSidebar={true}><FeatureManagementPage /></MainLayout>
                </ProtectedRoute>
              }
            />

            {/* PartnerAdmin Protected */}
            <Route
              path="/partnerDashboard"
              element={
                <ProtectedRoute roles={['PartnerAdmin']}>
                  <MainLayout showSidebar={true}><PartnerDashboard /></MainLayout>
                </ProtectedRoute>
              }
            />

            {/* Property Management */}
            <Route
              path="/property/:propertyId"
              element={
                <ProtectedRoute roles={['PartnerAdmin']}>
                  <MainLayout showSidebar={true}><PropertyOverviewPage /></MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/property/:propertyId/codes"
              element={
                <ProtectedRoute roles={['PartnerAdmin']}>
                  <MainLayout showSidebar={true}><PropertyCodesPage /></MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/property/:propertyId/users"
              element={
                <ProtectedRoute roles={['PartnerAdmin']}>
                  <MainLayout showSidebar={true}><PropertyUsersPage /></MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/property/:propertyId/settings"
              element={
                <ProtectedRoute roles={['PartnerAdmin']}>
                  <MainLayout showSidebar={true}><PropertySettingsPage /></MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/property/:propertyId/api-management"
              element={
                <ProtectedRoute roles={['Admin','PartnerAdmin']}>
                  <MainLayout showSidebar={true}>
                    <FeatureGuard 
                      featureName="api-access"
                      customMessage="API Access is not enabled for your account. This feature allows you to integrate with WorldExplore's REST API."
                    >
                      <PartnerApiManagementPage />
                    </FeatureGuard>
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/property/:propertyId/guest-trips"
              element={
                <ProtectedRoute roles={['Admin','PartnerAdmin']}>
                  <MainLayout showSidebar={true}>
                    <FeatureGuard 
                      featureName="guest-trip-management"
                      customMessage="Guest Trip Management is not enabled for your account. This feature allows you to create and manage guest trips with pre-assigned guides."
                    >
                      <GuestTripsPage />
                    </FeatureGuard>
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            {/* Subscription Management Routes */}
            <Route
              path="/partner/subscription"
              element={
                <ProtectedRoute roles={['PartnerAdmin', 'Admin']}>
                  <MainLayout showSidebar={true}><SubscriptionManagementPage /></MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription/success"
              element={
                <ProtectedRoute roles={['PartnerAdmin', 'Admin']}>
                  <MainLayout showSidebar={true}><SubscriptionSuccessPage /></MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription/cancel"
              element={
                <ProtectedRoute roles={['PartnerAdmin', 'Admin']}>
                  <MainLayout showSidebar={true}><SubscriptionCancelPage /></MainLayout>
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </AuthErrorBoundary>
        </Suspense>
      </PullToRefresh>

      {/* Other global components always mounted */}
      <Analytics />
      <CookieConsent />
      <UpdateNotification />
      <InstallPrompt />
    </ErrorBoundary>
  );
}

export default App;
